import { doc, setDoc, getDoc, query, collection, where, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { validateUsernameContent } from './usernameModeration';

/**
 * Check if a username is already taken
 * @param {string} username - The username to check
 * @returns {Promise<boolean>} - True if username exists, false otherwise
 */
export async function isUsernameTaken(username) {
  const usersRef = collection(db, 'users');
  const q = query(usersRef, where('username', '==', username));
  const querySnapshot = await getDocs(q);
  return !querySnapshot.empty;
}

/**
 * Save user profile to Firestore after signup
 * @param {string} userId - The Firebase Auth user ID
 * @param {string} email - The user's email
 * @param {string} username - The user's chosen username
 * @returns {Promise<void>}
 */
export async function saveUserProfile(userId, email, username) {
  try {
    // Validate username content (profanity filter and reserved names)
    const usernameValidation = validateUsernameContent(username);
    if (!usernameValidation.isValid) {
      throw new Error(usernameValidation.reason);
    }

    // Check if username is already taken
    const usernameTaken = await isUsernameTaken(username);
    if (usernameTaken) {
      throw new Error('Username is already taken. Please choose another.');
    }

    // Create user document in Firestore
    const userRef = doc(db, 'users', userId);

    await setDoc(userRef, {
      email: email,
      username: username,
      createdAt: new Date(),
      isVerified: false, // Will be updated when email is verified
      stats: {
        gamesPlayed: 0,
        gamesWon: 0,
        gamesLost: 0,
        currentStreak: 0,
        maxStreak: 0,
        totalAttempts: 0,
        avgScore: 0,
        lastPlayed: null
      }
    });

    return true;
  } catch (error) {
    console.error('Error saving user profile:', error);
    throw error;
  }
}

/**
 * Get user profile from Firestore
 * @param {string} userId - The Firebase Auth user ID
 * @returns {Promise<Object|null>} - User profile data or null
 */
export async function getUserProfile(userId) {
  try {
    const userRef = doc(db, 'users', userId);
    const userDoc = await getDoc(userRef);

    if (userDoc.exists()) {
      return userDoc.data();
    }
    return null;
  } catch (error) {
    console.error('Error getting user profile:', error);
    throw error;
  }
}

/**
 * Update user's verification status
 * @param {string} userId - The Firebase Auth user ID
 * @returns {Promise<void>}
 */
export async function updateUserVerificationStatus(userId) {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, { isVerified: true }, { merge: true });
  } catch (error) {
    console.error('Error updating verification status:', error);
    throw error;
  }
}
